import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Loader2, Download, Calendar, ArrowUpDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const SalesSummaryReport = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sort, setSort] = useState({ field: 'total_invoiced', dir: 'desc' });
  const printRef = React.useRef(null);

  useEffect(() => {
    const now = new Date();
    const past = new Date(now);
    past.setDate(past.getDate() - 90);
    setToDate(now.toISOString().slice(0, 10));
    setFromDate(past.toISOString().slice(0, 10));
  }, []);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await fetch(`${API_URL}/api/reports/sales-summary?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load report');
      setData(await res.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [fromDate, toDate]);

  const toggleSort = (field) => {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
  };

  const sortedRows = data ? [...data.rows].sort((a, b) => {
    const cmp = sort.field === 'customer_name'
      ? a.customer_name.localeCompare(b.customer_name)
      : a[sort.field] - b[sort.field];
    return sort.dir === 'desc' ? -cmp : cmp;
  }) : [];

  const handlePDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: `Sales_Summary_${fromDate}_${toDate}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
      }).from(printRef.current).save();
      toast.success('PDF downloaded');
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  // Top 5 customers bar chart data
  const top5 = data ? data.rows.slice(0, 5) : [];
  const maxInvoiced = top5.length > 0 ? Math.max(...top5.map(r => r.total_invoiced)) : 1;

  const cols = [
    { key: 'customer_name', label: 'Customer', align: 'left', sortable: true },
    { key: 'invoice_count', label: '# Invoices', align: 'right', sortable: true },
    { key: 'total_invoiced', label: 'Total Invoiced', align: 'right', sortable: true },
    { key: 'total_paid', label: 'Total Paid', align: 'right', sortable: true },
    { key: 'total_outstanding', label: 'Outstanding', align: 'right', sortable: true },
  ];

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="sales-summary-print"], [data-testid="sales-summary-print"] * { visibility: visible; }
          [data-testid="sales-summary-print"] { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          [data-testid="no-print"] { display: none !important; }
        }
      `}</style>

      <div className="space-y-6" data-testid="sales-summary-page">
        {/* Controls */}
        <div className="bg-white rounded-lg border p-6" data-testid="no-print">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#8B7355]" />
            Sales Summary Report
          </h2>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">From</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                  className="pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                  data-testid="sales-from-date" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">To</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                  className="pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                  data-testid="sales-to-date" />
              </div>
            </div>
            <Button onClick={generate} disabled={loading} className="bg-[#8B7355] hover:bg-[#6B5B4A] text-white" data-testid="sales-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
            {data && (
              <>
                <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="sales-print-btn">Print</Button>
                <Button variant="outline" size="sm" onClick={handlePDF} disabled={exporting} data-testid="sales-pdf-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} PDF
                </Button>
              </>
            )}
          </div>

          {/* Quick Periods */}
          <div className="mt-4 flex gap-2 flex-wrap">
            {[
              { label: 'Last 30 Days', days: 30 },
              { label: 'Last 90 Days', days: 90 },
              { label: 'This Month', preset: 'month' },
              { label: 'Last Month', preset: 'last-month' },
              { label: 'This Year', preset: 'year' },
            ].map(p => (
              <button key={p.label} data-testid={`sales-quick-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => {
                  const now = new Date();
                  let from, to;
                  if (p.days) { to = now; from = new Date(now); from.setDate(from.getDate() - p.days); }
                  else if (p.preset === 'month') { from = new Date(now.getFullYear(), now.getMonth(), 1); to = now; }
                  else if (p.preset === 'last-month') { from = new Date(now.getFullYear(), now.getMonth() - 1, 1); to = new Date(now.getFullYear(), now.getMonth(), 0); }
                  else if (p.preset === 'year') { from = new Date(now.getFullYear(), 0, 1); to = now; }
                  setFromDate(from.toISOString().slice(0, 10));
                  setToDate(to.toISOString().slice(0, 10));
                }}
                className="px-3 py-1.5 text-xs rounded-full border border-[#8B7355]/30 text-[#8B7355] hover:bg-[#8B7355]/10 transition-colors">
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Report */}
        {data && (
          <div ref={printRef} data-testid="sales-summary-print" className="bg-white rounded-lg border" style={{ fontFamily: 'Arial, sans-serif' }}>
            <div className="flex justify-between items-start p-8 pb-4">
              <div>
                <h1 className="text-2xl font-bold text-[#8B7355]">Sales Summary</h1>
                <p className="text-sm text-gray-500 mt-1">{data.period.from} to {data.period.to} | {data.customer_count} customer{data.customer_count !== 1 ? 's' : ''}</p>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="border rounded-lg p-3">
                  <p className="text-xs text-gray-500">Invoiced</p>
                  <p className="text-lg font-bold" data-testid="sales-total-invoiced">{fmt(data.totals.total_invoiced)}</p>
                </div>
                <div className="border rounded-lg p-3">
                  <p className="text-xs text-gray-500">Paid</p>
                  <p className="text-lg font-bold text-green-600">{fmt(data.totals.total_paid)}</p>
                </div>
                <div className="border rounded-lg p-3">
                  <p className="text-xs text-gray-500">Outstanding</p>
                  <p className="text-lg font-bold text-[#8B7355]">{fmt(data.totals.total_outstanding)}</p>
                </div>
              </div>
            </div>

            {/* Top 5 bar chart */}
            {top5.length > 0 && (
              <div className="px-8 pb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Top 5 Customers by Revenue</p>
                <div className="space-y-2">
                  {top5.map((r, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`sales-bar-${i}`}>
                      <span className="text-xs text-gray-600 w-36 truncate">{r.customer_name}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                        <div className="h-full bg-[#8B7355] rounded transition-all" style={{ width: `${(r.total_invoiced / maxInvoiced) * 100}%` }} />
                      </div>
                      <span className="text-xs font-semibold w-24 text-right">{fmt(r.total_invoiced)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-8 pb-6">
              <table className="w-full text-sm border-collapse" data-testid="sales-summary-table">
                <thead>
                  <tr className="bg-[#f5f5f0] border-y">
                    {cols.map(col => (
                      <th key={col.key}
                        className={`px-3 py-2 font-semibold text-gray-700 text-${col.align} ${col.sortable ? 'cursor-pointer select-none' : ''}`}
                        onClick={col.sortable ? () => toggleSort(col.key) : undefined}>
                        <span className="flex items-center gap-1" style={{ justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                          {col.label}
                          {col.sortable && <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50/60" data-testid={`sales-row-${i}`}>
                      <td className="px-3 py-2 font-medium">{row.customer_name}</td>
                      <td className="px-3 py-2 text-right">{row.invoice_count}</td>
                      <td className="px-3 py-2 text-right">{fmt(row.total_invoiced)}</td>
                      <td className="px-3 py-2 text-right text-green-600">{row.total_paid > 0 ? fmt(row.total_paid) : '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmt(row.total_outstanding)}</td>
                    </tr>
                  ))}
                  {sortedRows.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No sales data in this period</td></tr>
                  )}
                  {sortedRows.length > 0 && (
                    <tr className="bg-[#f5f5f0] border-t-2 border-gray-300 font-bold">
                      <td className="px-3 py-2">TOTALS</td>
                      <td className="px-3 py-2 text-right">{data.totals.invoice_count}</td>
                      <td className="px-3 py-2 text-right">{fmt(data.totals.total_invoiced)}</td>
                      <td className="px-3 py-2 text-right text-green-600">{fmt(data.totals.total_paid)}</td>
                      <td className="px-3 py-2 text-right text-[#8B7355]">{fmt(data.totals.total_outstanding)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-8 pb-6">
              <p className="text-xs text-gray-400">Report generated on {new Date().toLocaleDateString()} | Amounts in USD</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SalesSummaryReport;
